import { useEffect, useState } from "react";
import { Card, Table, TableHead, TableBody, TableRow, TableCell, TextField, Typography } from "@mui/material";

import useFetch from "../../api/api";
import config from "../../common/Config";
import Loading from "../Loading";

export default function ValuationComparison({ selectedStock, siblingStocks, company, indicators }) {

	const privateFetch = useFetch()

	const [editingTicker, setEditingTicker] 	= useState(null);
	const [tickerInput, setTickerInput]		 	= useState("");
	const [tickerIndex, setTickerIndex]			= useState(-1);
	const [customTickers, setCustomTickers] 	= useState([]);
	const [tickers, setTickers]					= useState([])
	const [isLoading, setIsLoading] 			= useState(true);


	const [data, setData] 						= useState([]);
	const [indicatorMap, setIndicatorMap] 		= useState({});
	const [indicatorsList, setIndicatorsList] 	= useState([]);
	const [stats, setStats] 					= useState({});

	useEffect(() => {
		buildValuationData()
	}, [])

	useEffect(() => {
		buildValuationData()
	}, [indicators, customTickers])

	const buildValuationData = async () => {
		const requiredMetrics = ['price', 'graham', 'priceEarnings', 'evEbitda', 'bookValue', 'tangibleBookValue', 'priceToBookValue', 'priceToTangibleBookValue', 'priceGraham', 'dividendYield'];
		const currentData = [];
		
		// Filtra apenas os indicadores importantes para valuation
		if (!indicators || indicators.length === 0) {
			return
		} 
		else {
			const filteredIndicators = indicators.filter(d => requiredMetrics.includes(d.id));
			currentData.push({ticker : selectedStock, indicators: filteredIndicators})
		}

		// Coleta todos os IDs de indicadores disponíveis
		const currentIndicatorMap = {};
		currentData.forEach((company) => {
			company.indicators.forEach((ind) => {
			currentIndicatorMap[ind.id] = ind.label;
			});
		});

		const currentIndicatorsList = Object.keys(currentIndicatorMap);

		// Adiciona empresas pares
		await privateFetch(config.apiUrl +'/api/indicators/companyPeers/'+ company.industry)
		.then((peers) => peers.json())
		.then((peers) => { 
			// Ordena por market cap e pega os 4 maiores diferentes da empresa selecionada
			const sortedPeers = peers
				.filter(p => !siblingStocks.includes(p.ticker))
				.sort((a, b) => b.marketCap - a.marketCap)
				.filter((p, index, arr) => index === arr.findIndex(x => x.name === p.name))
				.slice(0, 3);

			sortedPeers.map((peer) => {
				currentData.push({
					ticker : peer.ticker, 
					indicators: requiredMetrics.map(metricId => ({
						id: 	metricId,
						value: 	peer[metricId],
					}))
				})
			})
		})

		// Se nao há pares suficientes no segmento, busca do IBOV ou SP500
		if (currentData.length <= 4) {
			await privateFetch(config.apiUrl +'/api/indicators/companyPeers/'+ (company.country === 'BR' ? 'IBOV' : 'SP500'))
			.then((peers) => peers.json())
			.then((peers) => { 
				// Ordena por market cap e pega os 4 maiores diferentes da empresa selecionada
				const sortedPeers = peers
					.filter(p => !siblingStocks.includes(p.ticker))
					.sort((a, b) => b.marketCap - a.marketCap)
					.filter((p, index, arr) => index === arr.findIndex(x => x.name === p.name))
					.slice(0, 3);

				for (let i=0;currentData.length < 4;i++) {
					const peer = sortedPeers[i]

					currentData.push({
						ticker : peer.ticker, 
						indicators: requiredMetrics.map(metricId => ({
							id: 	metricId,
							value: 	peer[metricId],
						}))
					})
				}
			})		
		}

		// Se o usuario customizou empresas, busca indicadores
		await privateFetch(config.apiUrl +'/api/indicators/company/'+ customTickers.map(t => t.value).join(','))
		.then((res) => res.json())
		.then((res) => {
			res.map((company) => {
				const index = customTickers.find(t => t.value === company.ticker)?.index || -1;
				currentData[index] = {
					ticker : company.ticker, 
					indicators: requiredMetrics.map(metricId => ({
						id: 	metricId,
						value: 	company[metricId],
					}))
				}
			})
		})

		// Adicionar setor e indice


		// Calcula min e max por indicador
		const currentStats = currentIndicatorsList.reduce((acc, indicatorId) => {
			const type = indicators.find(i => i.id === indicatorId)?.comparisonType || "SMALLISBETTER";

			const values = currentData
			.map((company) => company.indicators.find((i) => i.id === indicatorId))
			.map((i) => (i ? Number(i.value) : null))
			.filter((v) => v !== null && !isNaN(v));

			if (values.length > 0) {
				const min = Math.min(...values);
				const max = Math.max(...values);				

				acc[indicatorId] = {
					min,
					max,
					best			: type === "BIGISBETTER" ? max : min,
					worst			: type === "BIGISBETTER" ? min : max,
					comparisonType	: type		
				};
			}
			return acc;
		}, {});

		setTickers(currentData.map(d => d.ticker))
		setIndicatorMap(currentIndicatorMap);
		setIndicatorsList(currentIndicatorsList);
		setStats(currentStats);
		setData(currentData);
		setIsLoading(false);
	}

	const handleTickerEdit = (index, value) => {
		setTickerInput(value.toUpperCase());
		setTickerIndex(index);
	};

	const startEdit = (ticker) => {
		setEditingTicker(ticker);
		setTickerInput(ticker);
	};

	const commitEdit = () => {
		setIsLoading(true);

		const updatedTickers = [...customTickers]
		const index = updatedTickers.findIndex(t => t.index === tickerIndex) 
			
		if (index >= 0) {
			updatedTickers[index] = {index: tickerIndex, value: tickerInput}
		}
		else {
			updatedTickers.push({index: tickerIndex, value: tickerInput})
		}

		setTickers(prev => {
			const copy = [...prev];
			copy[tickerIndex] = tickerInput;
			return copy;
		})
		setCustomTickers(updatedTickers);
		setEditingTicker(null);
		setTickerInput("");
		setTickerIndex(-1);		
	};

	return (
		<Card sx={{ p: 2, overflowX: "auto", width: "100%" }}>
			<Typography variant="h5" sx={{ mb: 2 }}>
				Valuation por Múltiplos
			</Typography>
			<Table size="small">
				<TableHead>
				<TableRow>
					<TableCell sx={{ fontWeight: 600 }}>Indicador</TableCell>
					{tickers.map((ticker, index) => (
					<TableCell key={ticker} align="center" sx={{ fontWeight: 600 }}>
						{editingTicker === ticker ? (
							<TextField
								size="small"
								value={tickerInput}
								onChange={(e) => handleTickerEdit(index, e.target.value)}
								onBlur={commitEdit}
								onKeyDown={(e) => e.key === "Enter" && commitEdit()}
								autoFocus
								sx={{
									width: 60,
									textAlign: "center",
									"& input": { textAlign: "center", p: 0.3, fontSize: "0.80rem" } 
								}}
							/>						
						) : (
							<span
								style={index > 0 ? { cursor: "pointer", textDecoration: "underline" } : {}}
								onClick={index > 0 ? () => startEdit(ticker) : null}
								>
								{ticker}
							</span>
						)}
					</TableCell>
					))}
				</TableRow>
				</TableHead>

				<TableBody>
				{isLoading ? (
					<TableRow><TableCell colSpan={999} align="center"><Loading /></TableCell></TableRow>
				) : (
					indicatorsList.map((indicatorId) => (
						<TableRow key={indicatorId}>
						<TableCell sx={{ fontWeight: 500 }}>{indicatorMap[indicatorId]}</TableCell>

						{data.map((company) => {
							const indicator = company.indicators.find((i) => i.id === indicatorId);
							const value = indicator ? Number(indicator.value) : null;
							const metricStats = stats[indicatorId];

							const isBest = metricStats && value === metricStats.best;
							const isWorst = metricStats && value === metricStats.worst;

							return (
							<TableCell key={company.ticker} align="center">
								{value !== null ? (
									<div style={{
										display: "inline-block",
										padding: "2px 6px",
										borderRadius: 4,
										backgroundColor: isBest ? "#bbf7d0" : isWorst ? "#fecaca" : "#e5e7eb",
										color: isBest ? "#064e3b" : isWorst ? "#7f1d1d" : "#374151",
										fontSize: "0.8rem",
									}}>
										{parseFloat(value).toFixed(2)}
									</div>							
								) : (
								"—"
								)}
							</TableCell>
							);
						})}
						</TableRow>
					))
				)}
				
				</TableBody>
			</Table>
		</Card>
	);
}
